import { useCallback, useEffect, useState } from 'react';
import {
  AgxButton,
  AgxColumn,
  AgxModal,
  AgxSegmentPicker,
  AgxSpacer,
  AgxTypography,
  Icon_AlertCircle,
} from '@urbanx/agx.ui-components2';
import {
  CampaignDetailModelState,
  Deposit,
  Condition,
  ConditionType,
  DepositPayable,
  DepositType,
  ValueType,
  useStaffGetContractDates,
  useStaffGetSaleInformation,
} from '@urbanx/agx.campaigns.client';
import { FormConditionOnDate } from '../components/FormConditionOnDate';
import FormConditionNDays from '../components/FormConditionNDays';
import { useSelectedCampaign, useSelectedContract } from 'hooks';
import { QldDepositOptionsComponent } from './QldDepositOptions';
import {
  NswDepositOptionsComponent,
  NswDepositPayableOption,
  NswDepositSelection,
} from './NswDepositOptions';
import {
  getAmountFromDeposit,
  getNswBalanceDepositDueDate,
  getNswDepositSplit,
} from '../depositCalculations';
import { toDateOnlyString } from '@urbanx/agx.ui-components2';

interface AddDepositButtonProps {
  id: string;
  label: string;
  depositType: DepositType;
  onAddDeposits: (initialDeposit: Deposit, balanceDeposit: Deposit | undefined) => void;
  disabled?: boolean;
}

const SegmentOptions = [
  { title: 'on...', value: DepositPayable.On },
  { title: '# days from...', value: DepositPayable.NDaysFrom },
];

export function AddDepositButton({
  id,
  label,
  depositType,
  onAddDeposits,
  disabled,
}: AddDepositButtonProps) {
  const { campaign } = useSelectedCampaign();
  const { contract } = useSelectedContract();
  const isNsw = campaign?.state === CampaignDetailModelState.NSW;
  const isQld = campaign?.state === CampaignDetailModelState.QLD;

  const { data: contractDates } = useStaffGetContractDates(
    campaign?.campaignId || '',
    contract?.contractId || '',
    !!campaign?.campaignId && !!contract?.contractId && isNsw
  );

  const { data: saleInformation } = useStaffGetSaleInformation(
    campaign?.campaignId || '',
    contract?.contractId || '',
    !!campaign?.campaignId && !!contract?.contractId && isNsw
  );

  const nswBalanceDepositDueDate = isNsw
    ? getNswBalanceDepositDueDate(contractDates?.expectedUnconditionalDate)
    : undefined;

  const [openDepositModal, setOpenDepositModal] = useState<boolean>(false);
  const [deposit, setDeposit] = useState<Deposit | undefined>(undefined);
  const [selectedSegment, setSelectedSegment] = useState<string>(DepositPayable.NDaysFrom);
  const [internalValidate, setInternalValidate] = useState<boolean>(false);
  const [internalErrorMessage, setInternalErrorMessage] = useState<string | undefined>(undefined);
  const [nswSelection, setNswSelection] = useState<NswDepositSelection | undefined>(undefined);

  const isNswFullUpfront =
    isNsw && nswSelection?.depositPayableOption === NswDepositPayableOption.FullDepositUpfront;

  useEffect(() => {
    setInternalValidate(false);
  }, [deposit]);

  useEffect(() => {
    setInternalErrorMessage(undefined);
    setDeposit(undefined);
    setInternalValidate(false);
    setNswSelection(undefined);
  }, [openDepositModal]);

  useEffect(() => {
    setInternalErrorMessage(undefined);
    setInternalValidate(false);
  }, [nswSelection]);

  const onSelectedSegmentChange = useCallback((segment: string) => {
    setSelectedSegment(segment);
    setDeposit((prev) => ({
      ...prev,
      depositPayable: segment as DepositPayable,
    }));
  }, []);

  const onConditionValueChange = useCallback((updatedValue: Deposit | unknown) => {
    const conditionUpdate = updatedValue as Deposit;
    setDeposit((prev) => ({
      ...prev,
      condition: conditionUpdate.condition,
      conditionType: conditionUpdate.conditionType,
      referenceDate: conditionUpdate.referenceDate,
      daysFromDate: conditionUpdate.daysFromDate,
      specialConditionId: conditionUpdate.specialConditionId,
      depositPayable: conditionUpdate.depositPayable,
    }));
  }, []);

  const onConfirmClick = useCallback(() => {
    setInternalValidate(true);

    if (isNsw && nswSelection) {
      const hasAmount =
        nswSelection.depositValueType === ValueType.Percentage
          ? nswSelection.percent !== undefined && nswSelection.percent > 0
          : nswSelection.amount !== undefined && nswSelection.amount > 0;

      if (!hasAmount) {
        setInternalErrorMessage('Enter a deposit amount');
        return;
      }

      const salePrice = saleInformation?.salePrice || 0;
      const quarterPercentAmount = getAmountFromDeposit({ percent: 0.25 }, salePrice);

      if (
        nswSelection.depositValueType === ValueType.Percentage &&
        (!salePrice || salePrice <= 0)
      ) {
        setInternalErrorMessage('Sale price is required to calculate deposit amount');
        return;
      }

      if (!isNswFullUpfront) {
        if (!salePrice || salePrice <= 0) {
          setInternalErrorMessage('Sale price is required to calculate 0.25% initial deposit');
          return;
        }

        if (nswSelection.depositValueType === ValueType.Dollar) {
          // Dollar-based: total deposit must be at least 0.25% of sale price
          const minimumAmount = Math.ceil(quarterPercentAmount * 100) / 100;
          if (nswSelection.amount !== undefined && nswSelection.amount < minimumAmount) {
            setInternalErrorMessage(
              `Minimum deposit amount is $${minimumAmount.toFixed(2)} (0.25% of sale price)`
            );
            return;
          }
        } else {
          // Percentage-based: total deposit must be at least 0.25%
          if (nswSelection.percent !== undefined && nswSelection.percent < 0.25) {
            setInternalErrorMessage('Minimum deposit percentage is 0.25%');
            return;
          }
        }
      }

      const totalDepositAmount = getAmountFromDeposit(nswSelection, salePrice);
      const { initialDepositAmount, balanceDepositAmount } = getNswDepositSplit({
        totalDepositAmount,
        salePrice,
        fullDepositUpfront: isNswFullUpfront,
      });

      const initialDeposit: Deposit = {
        depositType: DepositType.Initial,
        depositValueType: ValueType.Dollar,
        percent: undefined,
        amount: initialDepositAmount,
        condition: Condition.OnDate,
        conditionType: ConditionType.Contract,
        isDepositSelected: true,
      };

      // Create balance deposit only if 0.25% upfront is selected
      // Balance deposit is due on the expected unconditional date.
      // Balance is the total deposit minus the 0.25% initial deposit
      let balanceDeposit: Deposit | undefined;
      if (!isNswFullUpfront && balanceDepositAmount) {
        balanceDeposit = {
          depositType: DepositType.Balance,
          depositValueType: ValueType.Dollar,
          percent: undefined,
          amount: balanceDepositAmount,
          condition: Condition.OnDate,
          conditionType: ConditionType.Unconditional,
          referenceDate: toDateOnlyString(nswBalanceDepositDueDate),
          isDepositSelected: true,
        };
      }

      if (onAddDeposits) {
        onAddDeposits(initialDeposit, balanceDeposit);
      }

      setOpenDepositModal(false);
      setInternalValidate(false);
      setInternalErrorMessage(undefined);
      return;
    }

    if (!deposit) {
      setInternalErrorMessage('Enter a deposit amount');
      return;
    }

    const hasAmount =
      deposit.depositValueType === ValueType.Percentage
        ? deposit.percent !== undefined && deposit.percent > 0
        : deposit.amount !== undefined && deposit.amount > 0;

    if (!hasAmount) {
      setInternalErrorMessage('Enter a deposit amount');
      return;
    }

    if (!deposit.condition) {
      setInternalErrorMessage('Select a condition');
      return;
    }

    const conditionStr = deposit.condition as string;

    if (conditionStr === 'OnSpecificDate' && !deposit.referenceDate) {
      setInternalErrorMessage('Select a date');
      return;
    } else if (conditionStr === 'NdaysFromDate' && !deposit.daysFromDate) {
      setInternalErrorMessage('Enter number of days');
      return;
    } else if (
      conditionStr === 'UponSatisfactionOfSpecialCondition' &&
      !deposit.specialConditionId
    ) {
      setInternalErrorMessage('Enter special condition clause');
      return;
    }

    onAddDeposits(
      depositType === DepositType.Initial
        ? {
            ...deposit,
            depositType: DepositType.Initial,
            isDepositSelected: true,
          }
        : {},
      depositType === DepositType.Balance
        ? {
            ...deposit,
            depositType: DepositType.Balance,
            isDepositSelected: true,
          }
        : undefined
    );
    setOpenDepositModal(false);
    setInternalValidate(false);
    setInternalErrorMessage(undefined);
  }, [
    deposit,
    onAddDeposits,
    depositType,
    isNsw,
    isNswFullUpfront,
    nswSelection,
    nswBalanceDepositDueDate,
    saleInformation,
  ]);

  return (
    <>
      <AgxButton
        id={`${id}-add-deposit`}
        variant="hollow"
        text={label}
        disabled={disabled}
        onClick={() => setOpenDepositModal(true)}
      />
      {openDepositModal && (
        <AgxModal
          title={label}
          variant="withDivider"
          type="single"
          actionButtonContainerStyle="withoutDivider"
          setIsDialogOpen={setOpenDepositModal}
          content={
            <AgxColumn
              gap="spacing-lg"
              padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
            >
              <AgxSpacer id={id} direction="vertical" gap="md" fill>
                {isQld && (
                  <QldDepositOptionsComponent
                    id={id}
                    onDepositChange={setDeposit}
                    depositType={depositType}
                  />
                )}
                {isNsw && (
                  <NswDepositOptionsComponent id={id} onSelectionChange={setNswSelection} />
                )}
                {!isNsw && (
                  <AgxColumn gap="spacing-md" fill>
                    <AgxSegmentPicker
                      label={`${depositType === DepositType.Balance ? 'Balance' : 'Initial'} Deposit Payable *`}
                      id={`${id}-depositPayableSegment`}
                      segments={SegmentOptions}
                      onChange={onSelectedSegmentChange}
                    />
                    {selectedSegment === DepositPayable.On && (
                      <FormConditionOnDate
                        id={id}
                        onSpecificDateOptions={
                          depositType === DepositType.Initial
                            ? [
                                Condition.OnDate,
                                Condition.OnSpecificDate,
                                Condition.FinanceBeingSatisfied,
                                Condition.BuildingAndPestBeingSatisfied,
                                Condition.UponSatisfactionOfSpecialCondition,
                              ]
                            : [
                                Condition.NdaysFromDate,
                                Condition.OnSpecificDate,
                                Condition.FinanceBeingSatisfied,
                                Condition.BuildingAndPestBeingSatisfied,
                                Condition.UponSatisfactionOfSpecialCondition,
                              ]
                        }
                        defaultValue={deposit}
                        onValueChange={onConditionValueChange}
                        conditionTypeOverride={
                          depositType === DepositType.Initial ? ConditionType.Contract : undefined
                        }
                        depositType={depositType}
                      />
                    )}
                    {selectedSegment === DepositPayable.NDaysFrom && (
                      <FormConditionNDays
                        id={id}
                        fromDateOptions={[
                          Condition.NdaysFromDate,
                          Condition.FinanceBeingSatisfied,
                          Condition.BuildingAndPestBeingSatisfied,
                          Condition.UponSatisfactionOfSpecialCondition,
                        ]}
                        defaultValue={deposit}
                        hasPriceOrPercentage={false}
                        onValueChange={onConditionValueChange}
                        validate={internalValidate}
                        conditionTypeOverride={
                          depositType === DepositType.Initial ? ConditionType.Contract : undefined
                        }
                        depositType={depositType}
                      />
                    )}
                  </AgxColumn>
                )}
              </AgxSpacer>
              {internalValidate && internalErrorMessage && (
                <div className={'errorMessage'}>
                  <Icon_AlertCircle type="danger" />
                  <AgxTypography id={`${id}-error`} type="form_error" size="small">
                    {internalErrorMessage}
                  </AgxTypography>
                </div>
              )}
            </AgxColumn>
          }
          actionButtons={
            <AgxSpacer gap="spacing-md" direction="horizontal" fill padding={{ top: 'md' }}>
              <AgxButton
                id={`${id}-cancel-add-deposit`}
                variant="hollow"
                text="Cancel"
                size="large"
                stretch
                onClick={() => setOpenDepositModal(false)}
              />
              <AgxButton
                id={`${id}-save-add-deposit`}
                variant="primary"
                text="Save"
                size="large"
                stretch
                onClick={onConfirmClick}
                disabled={internalValidate && !!internalErrorMessage}
              />
            </AgxSpacer>
          }
        />
      )}
    </>
  );
}
